import {
  EllipsedText,
  EmptyState,
  FlexColumn,
  FlexRow,
  getFileNameFromPath,
  isValidImage,
  isValidLatitude,
  isValidLongitude,
  isValidVideo,
  LabeledData,
  Loading,
  StyledButton,
  useLoading,
  useMoment,
} from '@gorila-shared-ui/components';
import { StyledLink } from 'baseui/link';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'baseui/modal';
import { HeadingXSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRecoilValue } from 'recoil';
import { MEDIA_FILE_TYPE_OPTIONS, MEDIA_SAFE_GPT_ORIGIN } from '../../../../constants/media';
import { useFeedback } from '../../../../hooks/useFeedback';
import { useStyles } from '../../../../hooks/useStyles';
import { getS3FileUrlByKey } from '../../../../services/appService';
import { getDownloadMediaUrlByKey } from '../../../../services/evidenceService';
import { gptAlarmTypeNameState } from '../../../../storage/gptStates';
import { StreamingTimelineEvent } from '../../../../types/streaming';
import { downloadBlob } from '../../../../utils/files';
import { getMediaTypeLabel } from '../../../../utils/media';
import EvidenceMetadataDisplay from '../../../evidences/EvidenceMetadataDisplay';
import EvidenceMediaOriginTag from '../../../shared/evidences/EvidenceMediaOriginTag';

type Props = {
  event: StreamingTimelineEvent;
  onClose: () => void;
  isOpen: boolean;
};

const CapturingVideoEventMediaModal = ({ event, onClose, isOpen }: Readonly<Props>) => {
  const { css, theme, classes, concatenateClasses } = useStyles();
  const { startLoading: startDownloading, stopLoading: stopDownloading, loading: downloading } = useLoading();
  const { loading, startLoading, stopLoading } = useLoading(true);
  const { showFailFeedback } = useFeedback();
  const { formatDateFromTs } = useMoment();
  const [source, setSource] = useState('');
  const alarmType = useRecoilValue(gptAlarmTypeNameState(event?.alarmType));
  const hasValidCoords =
    !!event?.longitude && isValidLongitude(event.longitude) && !!event.latitude && isValidLatitude(event.latitude);

  const mediaWrapperStyles = css({ height: '230px', display: 'flex', justifyContent: 'center', alignItems: 'center' });
  const mediaStyles = css({ height: '100%', maxWidth: '100%', objectFit: 'contain' });
  const labelWidth = '70px';
  const modalFooterStyles = css({
    marginLeft: '0 !important',
    marginRight: '0 !important',
    paddingLeft: theme.sizing.scale800,
    paddingRight: theme.sizing.scale800,
    borderTop: `${theme.borders.border200.borderWidth} ${theme.borders.border200.borderStyle} ${theme.borders.border200.borderColor}`,
  });

  useEffect(() => {
    const loadMedia = async () => {
      if (!event.key) {
        stopLoading();
        return;
      }
      startLoading();
      const { url, error } = await getS3FileUrlByKey(event.key, event.mediaOrigin === MEDIA_SAFE_GPT_ORIGIN);
      if (!error && url) {
        setSource(url);
      }
      stopLoading();
    };

    loadMedia();
  }, [event.key, event.mediaOrigin]);

  const download = async () => {
    if (!event.key) return;
    startDownloading();
    const { file, error } = await getDownloadMediaUrlByKey(event.key, event.mediaOrigin);
    if (error) {
      showFailFeedback(error);
    } else if (file) {
      downloadBlob(file, getFileNameFromPath(event.key));
    }
    stopDownloading();
  };

  return createPortal(
    <Modal
      animate
      closeable
      onClose={onClose}
      isOpen={isOpen}
      size="auto"
      overrides={{
        Dialog: {
          style: {
            minWidth: '450px',
          },
        },
      }}
    >
      <ModalHeader>
        <FlexRow
          gap={theme.sizing.scale200}
          classNames={classes.centeredStart}
        >
          <HeadingXSmall margin={0}>Evento</HeadingXSmall>
          <EvidenceMediaOriginTag origin={event.mediaOrigin} />
        </FlexRow>
      </ModalHeader>
      <ModalBody>
        <FlexColumn>
          <FlexColumn gap={theme.sizing.scale200}>
            {event.fileType && (
              <LabeledData
                label={'Tipo:'}
                content={MEDIA_FILE_TYPE_OPTIONS.find((option) => option.id === event.fileType)?.label ?? '-'}
                labelWidth={labelWidth}
              />
            )}
            {alarmType && (
              <LabeledData
                label={'Alarma:'}
                content={alarmType}
                labelWidth={labelWidth}
              />
            )}
            <LabeledData
              label={'Fecha:'}
              content={`${formatDateFromTs(+event.created)}`}
              labelWidth={labelWidth}
            />
            <LabeledData
              label={'Dirección:'}
              labelWidth={labelWidth}
              content={
                hasValidCoords && event.geoReference ? (
                  <StyledLink
                    href={`https://maps.google.com/?q=${event.latitude},${event.longitude}`}
                    target="_blank"
                    style={{
                      color: theme.colors.contentSecondary,
                      ...theme.typography.ParagraphSmall,
                      maxWidth: '320px',
                    }}
                  >
                    <EllipsedText
                      breakAll
                      maxLines={3}
                    >
                      {event.geoReference}
                    </EllipsedText>
                  </StyledLink>
                ) : (
                  (event.geoReference ?? '-')
                )
              }
            />
          </FlexColumn>
          {event.key && (
            <div className={mediaWrapperStyles}>
              {loading && <Loading />}
              {!loading && !source && <EmptyState title="No disponible" />}
              {!loading && source && (
                <>
                  {isValidVideo(source) && (
                    <video
                      className={mediaStyles}
                      autoPlay={false}
                      controls
                      controlsList="nofullscreen nodownload"
                    >
                      <source
                        src={source}
                        type="video/mp4"
                      />
                    </video>
                  )}
                  {isValidImage(source) && (
                    <img
                      src={source}
                      alt=""
                      className={mediaStyles}
                    />
                  )}
                </>
              )}
            </div>
          )}
          <EvidenceMetadataDisplay
            metadata={event.metadata}
            url={event.key}
          />
        </FlexColumn>
      </ModalBody>
      {event.key && (
        <ModalFooter className={modalFooterStyles}>
          <FlexRow
            gap={theme.sizing.scale300}
            classNames={concatenateClasses(classes.centeredEnd, css({ maxWidth: '500px', margin: '0 auto' }))}
          >
            <StyledButton
              kind="secondary"
              onClick={onClose}
              full
            >
              Cerrar
            </StyledButton>
            <StyledButton
              onClick={download}
              isLoading={downloading}
              full
              disabled={!event.key}
            >
              Descargar {getMediaTypeLabel(event.key)}
            </StyledButton>
          </FlexRow>
        </ModalFooter>
      )}
    </Modal>,
    document.body,
  );
};

export default CapturingVideoEventMediaModal;
